import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../config";
import { Input } from "./ui/input";
import { AlertCircle, Check, RefreshCw, ArrowRight } from "lucide-react";

const EmailCapture = ({ darkMode, compact = false, source = "website" }) => {
  const dm = darkMode;
  const [email, setEmail] = useState("");
  const [consented, setConsented] = useState(false);
  const [reactivateConsented, setReactivateConsented] = useState(false);
  const [status, setStatus] = useState("idle");
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    if (!consented) { setStatus("error"); setMsg("Please accept the Privacy Policy to continue."); return; }
    setStatus("loading");
    try {
      const res = await axios.post(`${API}/auth/register`, {
        email: email.trim().toLowerCase(), privacy_consented: true, consent_source: source,
      });
      if (res.data.already_exists) {
        setStatus("exists"); setMsg("You're already a member — check your inbox for the magic link.");
      } else {
        setStatus("success");
        setMsg(res.data.email_sent ? "Check your inbox — magic link on its way." : "Account registered — the magic link could not be sent. Please contact the administrator.");
      }
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.detail === "newsletter_inactive") {
        setStatus("reactivate");
      } else {
        setStatus("error"); setMsg(err.response?.data?.detail || "Something went wrong — please try again.");
      }
    }
  };

  const reactivate = async (e) => {
    e.preventDefault();
    if (!reactivateConsented) return;
    setStatus("reactivating");
    try {
      const res = await axios.post(`${API}/auth/reactivate`, {
        email: email.trim().toLowerCase(), privacy_consented: true, consent_source: "reactivation",
      });
      setStatus("reactivated");
      setMsg(res.data.email_sent ? "Welcome back! Check your inbox — magic link on its way." : "Account reactivated — the magic link could not be sent. Please contact the administrator.");
    } catch (err) {
      setStatus("error"); setMsg(err.response?.data?.detail || "Reactivation failed — please try again.");
    }
  };

  if (status === "reactivate" || status === "reactivating") {
    return (
      <div className={`rounded-2xl p-5 space-y-4 ${dm ? "bg-[#1a1f20] border border-amber-400/20" : "bg-amber-50 border border-amber-200"}`}>
        <div className="flex items-start gap-3">
          <span className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${dm ? "bg-amber-400/10" : "bg-amber-100"}`}>
            <AlertCircle className={`w-4 h-4 ${dm ? "text-amber-400" : "text-amber-600"}`} />
          </span>
          <div>
            <p className={`text-sm font-semibold mb-1 ${dm ? "text-amber-400" : "text-amber-800"}`}>Your subscription was cancelled</p>
            <p className={`text-xs leading-relaxed ${dm ? "text-amber-400/70" : "text-amber-700"}`}>Access to TourismIntel is only available to active newsletter subscribers. Your previous subscription was cancelled, which also removed your platform access.</p>
          </div>
        </div>
        <div className={`rounded-xl p-4 space-y-1 text-xs ${dm ? "bg-[#001f24]/60 text-cyan-400/70 border border-cyan-400/10" : "bg-white text-[#006874]/80 border border-[#006874]/10"}`}>
          <p className="font-semibold">By resubscribing you confirm that:</p>
          <p>• You consent to receive the TourismIntel newsletter</p>
          <p>• You understand that your newsletter subscription is a condition for accessing this platform</p>
          <p>• You can unsubscribe at any time — this will also remove your platform access</p>
        </div>
        <form onSubmit={reactivate} className="space-y-3">
          <label className="flex items-start gap-2 cursor-pointer group">
            <input type="checkbox" checked={reactivateConsented} onChange={e => setReactivateConsented(e.target.checked)}
              data-testid="reactivate-consent-checkbox" className="mt-0.5 w-3.5 h-3.5 rounded flex-shrink-0 accent-[#006874]" />
            <span className={`text-xs leading-relaxed ${dm ? "text-slate-400" : "text-[#3b494c]"}`}>
              I agree to the{" "}
              <Link to="/privacy" target="_blank" className={`underline underline-offset-2 ${dm ? "text-cyan-400/80 hover:text-cyan-400" : "text-[#006874]/80 hover:text-[#006874]"}`}>Privacy Policy</Link>
              {" "}and consent to receive the TourismIntel newsletter in exchange for platform access.
            </span>
          </label>
          <div className="flex gap-2">
            <button type="button" onClick={() => { setStatus("idle"); setReactivateConsented(false); }}
              className={`flex-1 py-2 rounded-full category-tag transition-all ${dm ? "bg-[#252e2f] text-slate-400 hover:bg-[#303b3c]" : "bg-white text-[#6b7a7d] hover:bg-[#f3f4f5] border border-[#e7e8e9]"}`}>Cancel</button>
            <button type="submit" disabled={!reactivateConsented || status === "reactivating"} data-testid="reactivate-submit-btn"
              className={`flex-1 py-2 rounded-full category-tag transition-all disabled:opacity-50 flex items-center justify-center gap-1.5 ${dm ? "bg-cyan-400 text-[#001f24] hover:bg-cyan-300" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}>
              {status === "reactivating" ? <><RefreshCw className="w-3 h-3 animate-spin" />Reactivating…</> : <>Resubscribe & regain access <ArrowRight className="w-3 h-3" /></>}
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (status === "success" || status === "exists" || status === "reactivated") {
    const isEmailFailed = status === "success" && !msg.includes("on its way") && !msg.includes("Welcome back");
    return (
      <div className="flex items-center gap-2.5">
        <span className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${isEmailFailed ? "bg-amber-500" : "bg-emerald-500"}`}>
          {isEmailFailed ? <AlertCircle className="w-3.5 h-3.5 text-white" /> : <Check className="w-3.5 h-3.5 text-white" />}
        </span>
        <span className={`text-sm ${isEmailFailed ? (dm ? "text-amber-400" : "text-amber-700") : (dm ? "text-emerald-400" : "text-emerald-700")}`}>{msg}</span>
      </div>
    );
  }

  return (
    <div className="space-y-2.5 w-full">
      {status === "error" && (
        <p className="text-xs text-red-500 flex items-center gap-1.5" data-testid="subscribe-error-msg"><span>⚠</span> {msg}</p>
      )}
      <form onSubmit={submit} className="flex gap-2 w-full">
        <Input type="email" value={email} onChange={e => { setEmail(e.target.value); if (status === "error") setStatus("idle"); }}
          placeholder="your@email.com" required data-testid="subscribe-email-input"
          className={`border-0 flex-1 ${dm ? "bg-[#252e2f] text-slate-200 placeholder:text-slate-600" : "bg-[#f3f4f5] placeholder:text-[#bac9cc]"}`} />
        <button type="submit" disabled={status === "loading" || !consented} data-testid="subscribe-submit-btn"
          className={`px-5 py-2 rounded-full category-tag transition-all whitespace-nowrap disabled:opacity-60 flex items-center gap-1.5 ${dm ? "bg-cyan-400 text-[#001f24] hover:bg-cyan-300" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}>
          {status === "loading" ? <><RefreshCw className="w-3 h-3 animate-spin" />Joining…</> : <>Join free <ArrowRight className="w-3 h-3" /></>}
        </button>
      </form>
      <label className="flex items-start gap-2 cursor-pointer group" data-testid="privacy-consent-label">
        <input type="checkbox" checked={consented}
          onChange={e => { setConsented(e.target.checked); if (status === "error") setStatus("idle"); }}
          data-testid="privacy-consent-checkbox"
          className={`mt-0.5 w-3.5 h-3.5 rounded flex-shrink-0 accent-[#006874] ${status === "error" && !consented ? "outline outline-1 outline-red-400" : ""}`} />
        <span className={`text-xs leading-relaxed ${dm ? "text-slate-500 group-hover:text-slate-400" : "text-[#6b7a7d] group-hover:text-[#3b494c]"}`}>
          I agree to the{" "}
          <Link to="/privacy" target="_blank" className={`underline underline-offset-2 ${dm ? "text-cyan-400/80 hover:text-cyan-400" : "text-[#006874]/80 hover:text-[#006874]"}`}>Privacy Policy</Link>
          {" "}and consent to receive the TourismIntel newsletter.{" "}
          <span className={`font-medium ${dm ? "text-slate-400" : "text-[#3b494c]"}`}>Subscribing grants access to the platform — unsubscribing removes it.</span>
        </span>
      </label>
    </div>
  );
};

export default EmailCapture;
